// @architect-approved: HARDCODED_COLORS - Dedicated dark "Executive Access" console theme (space/night gradient, cyan accent #4FC3F7) is intentionally distinct from the app's light white-label theme, shared with ExecutiveQRLanding.tsx

// @architect-approved: INFRASTRUCTURE - ExecutiveQuickMenu.tsx does not follow component identity pattern
/**
 * [CONTAINER-START]
 * CONTAINER-TYPE: dashboard
 * CONTAINER-CLASS: executive-access
 * ROUTE: /executive-access
 * NAV-LABEL: Executive Quick Access
 * TG-SOLUTION-ID: 105
 * ASSOCIATION-TYPE: executive-only
 * DESCRIPTION: ATM-style quick menu for executives - Submit requests without full platform navigation
 * DEPENDENCIES: authService, Executive Access API
 * CONTAINER-STATUS: mvp-production
 * CONTAINER-VERSION: 1.0.0
 * [CONTAINER-END]
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import EventIcon from '@mui/icons-material/Event';
import MailIcon from '@mui/icons-material/Mail';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { SecureStorage } from '../../utils/SecureStorage';
interface MenuItem {
    menu_uuid: string;
    menu_title: string;
    menu_description: string;
    menu_category: string;
    icon_name: string;
    requires_otp: boolean;
}

interface RequestForm {
    title: string;
    description: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    priority: 'Low' | 'Medium' | 'High' | 'Urgent';
}

const ExecutiveQuickMenu = () => {
    const navigate = useNavigate();
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);

    const [formData, setFormData] = useState<RequestForm>({
        title: '',
        description: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        priority: 'Medium',
    });

    useEffect(() => {
        // Static menu items for MVP - no backend call needed
        setMenuItems([
            {
                menu_uuid: '1',
                menu_title: 'Development Request',
                menu_description: 'Submit a new development or feature request',
                menu_category: 'development',
                icon_name: 'bug_report',
                requires_otp: false,
            },
            {
                menu_uuid: '2',
                menu_title: 'QR Code Access',
                menu_description: 'Generate QR codes for quick access',
                menu_category: 'access',
                icon_name: 'qr_code_2',
                requires_otp: false,
            },
            {
                menu_uuid: '3',
                menu_title: 'Executive Menu',
                menu_description: 'Access executive-specific features',
                menu_category: 'executive',
                icon_name: 'business_center',
                requires_otp: false,
            },
        ]);
        setIsLoading(false);
    }, []);

    const getMenuIcon = (iconName: string) => {
        const icons: Record<string, React.ReactElement> = {
            bug_report: <BugReportIcon sx={{ fontSize: 60 }} />,
            person_add: <PersonAddIcon sx={{ fontSize: 60 }} />,
            support_agent: <SupportAgentIcon sx={{ fontSize: 60 }} />,
            integration_instructions: <IntegrationInstructionsIcon sx={{ fontSize: 60 }} />,
            event: <EventIcon sx={{ fontSize: 60 }} />,
            mail: <MailIcon sx={{ fontSize: 60 }} />,
        };
        return icons[iconName] || <MailIcon sx={{ fontSize: 60 }} />;
    };

    const handleMenuClick = (menu: MenuItem) => {
        setSelectedMenu(menu);
        setFormData({
            title: '',
            description: '',
            contactName: '',
            contactEmail: '',
            contactPhone: '',
            priority: 'Medium',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!selectedMenu) return;

        if (!formData.title || !formData.description) {
            toast.error('Please fill in title and description');
            return;
        }

        setIsSubmitting(true);

        try {
            const token = SecureStorage.getAccessToken();
            const pfuserUuid = SecureStorage.getPfuserUuid();

            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/executive/submit-request`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        pfuser_uuid: pfuserUuid,
                        menu_uuid: selectedMenu.menu_uuid,
                        request_title: formData.title,
                        request_description: formData.description,
                        contact_name: formData.contactName || null,
                        contact_email: formData.contactEmail || null,
                        contact_phone: formData.contactPhone || null,
                        priority: formData.priority,
                    }),
                },
            );

            if (!response.ok) throw new Error('Failed to submit request');

            setDialogOpen(false);
            setSuccessDialogOpen(true);
        } catch (error) {
            toast.error('Failed to submit request');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
        toast.info('Logged out successfully');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'radial-gradient(ellipse at center, #1a1a3e 0%, #0a0a1a 100%)',
                py: 4,
            }}
        >
            <Container maxWidth="lg">
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                    }}
                >
                    <Box>
                        <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                            Executive Quick Access
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#B0BEC5' }}>
                            Submit requests quickly without navigating the full platform
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogout}
                        sx={{
                            color: 'white',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            '&:hover': { borderColor: '#f44336', color: '#f44336' },
                        }}
                    >
                        Logout
                    </Button>
                </Box>

                {/* Menu Grid */}
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {menuItems.map((menu) => (
                            <Grid
                                key={menu.menu_uuid}
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 4,
                                }}
                            >
                                <Card
                                    sx={{
                                        background: 'rgba(0, 0, 0, 0.4)',
                                        backdropFilter: 'blur(15px)',
                                        border: '2px solid rgba(79, 195, 247, 0.3)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            border: '2px solid rgba(79, 195, 247, 0.8)',
                                            boxShadow: '0 12px 40px rgba(79, 195, 247, 0.4)',
                                        },
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleMenuClick(menu)}
                                        sx={{ p: 3 }}
                                    >
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            <Box sx={{ color: '#4FC3F7', mb: 2 }}>
                                                {getMenuIcon(menu.icon_name)}
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ color: 'white', fontWeight: 600, mb: 1 }}
                                            >
                                                {menu.menu_title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#B0BEC5', mb: 2 }}
                                            >
                                                {menu.menu_description}
                                            </Typography>
                                            {menu.requires_otp && (
                                                <Chip
                                                    label="OTP Required"
                                                    size="small"
                                                    sx={{
                                                        background: 'rgba(255, 152, 0, 0.2)',
                                                        color: '#FFA726',
                                                        borderColor: '#FFA726',
                                                    }}
                                                />
                                            )}
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Request Form Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={() => !isSubmitting && setDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                background: 'rgba(0, 0, 0, 0.9)',
                                backdropFilter: 'blur(15px)',
                                border: '2px solid rgba(79, 195, 247, 0.3)',
                            },
                        },
                    }}
                >
                    <DialogTitle
                        sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
                    >
                        {selectedMenu?.menu_title}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Alert
                            severity="info"
                            sx={{ mb: 2, background: 'rgba(33, 150, 243, 0.1)' }}
                        >
                            <Typography variant="body2" sx={{ color: 'white' }}>
                                {selectedMenu?.menu_description}
                            </Typography>
                        </Alert>

                        <TextField
                            fullWidth
                            label="Request Title"
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(79, 195, 247, 0.5)' },
                                    '&.Mui-focused fieldset': { borderColor: '#4FC3F7' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Description"
                            required
                            multiline
                            rows={4}
                            value={formData.description}
                            onChange={(e) =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(79, 195, 247, 0.5)' },
                                    '&.Mui-focused fieldset': { borderColor: '#4FC3F7' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            }}
                        />

                        {/* Contact fields (optional) */}
                        <Typography variant="subtitle2" sx={{ color: '#B0BEC5', mb: 1 }}>
                            Contact Information (Optional)
                        </Typography>

                        <TextField
                            fullWidth
                            label="Contact Name"
                            value={formData.contactName}
                            onChange={(e) =>
                                setFormData({ ...formData, contactName: e.target.value })
                            }
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(79, 195, 247, 0.5)' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Contact Email"
                            type="email"
                            value={formData.contactEmail}
                            onChange={(e) =>
                                setFormData({ ...formData, contactEmail: e.target.value })
                            }
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(79, 195, 247, 0.5)' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Contact Phone"
                            value={formData.contactPhone}
                            onChange={(e) =>
                                setFormData({ ...formData, contactPhone: e.target.value })
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(79, 195, 247, 0.5)' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Button
                            onClick={() => setDialogOpen(false)}
                            disabled={isSubmitting}
                            sx={{ color: '#B0BEC5' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{
                                background: 'linear-gradient(135deg, #4FC3F7 0%, #2196F3 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                                },
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} /> : 'Submit Request'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Success Dialog */}
                <Dialog
                    open={successDialogOpen}
                    onClose={() => setSuccessDialogOpen(false)}
                    maxWidth="xs"
                    slotProps={{
                        paper: {
                            sx: {
                                background: 'rgba(0, 0, 0, 0.9)',
                                backdropFilter: 'blur(15px)',
                                border: '2px solid rgba(76, 175, 80, 0.5)',
                                textAlign: 'center',
                            },
                        },
                    }}
                >
                    <DialogContent sx={{ py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
                        <Typography variant="h5" sx={{ color: 'white', mb: 1, fontWeight: 600 }}>
                            Request Submitted!
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#B0BEC5', mb: 3 }}>
                            Your request has been received and assigned to the appropriate team.
                            You'll receive updates via email.
                        </Typography>
                        <Button
                            onClick={() => setSuccessDialogOpen(false)}
                            variant="contained"
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
                                },
                            }}
                        >
                            Close
                        </Button>
                    </DialogContent>
                </Dialog>
            </Container>
        </Box>
    );
};

export default ExecutiveQuickMenu;
